/**
 * @module experience/billing-and-payment/billing-and-payment-service
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:max-file-line-count */
/* tslint:disable:no-any */ // Need to support all data types
// Core Libraries
import { EventEmitter, Injectable } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder } from '@angular/forms';
import { SelectItem } from 'primeng/api';

// Api endpoints
import { API_MAP } from '@core/api/proxies/common-api-endpoints';

// Interfaces
import { APIError } from '@support/api/common/error.interface';
import { PaymentMethodsData } from '@support/api/proxies/responses/api-144.interface';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { Api144Payload } from '@support/api/proxies/requests/api-144.interface';
import { SavePaymentMethod } from '@support/api/proxies/responses/api-202.interface';
import { BillingPreferenceResponse } from '@support/api/proxies/responses/api-2036.interface';
import { StaticModal } from '@support/shared/widgets/interfaces/static-modal.interface';
import { ChangeBillingLanguageInterface } from '@experience/modules/manage-profile/billing-and-payment/interfaces/change-billing-language.interface';
import { ChangeBillDeliveryInterface } from '@experience/modules/manage-profile/billing-and-payment/interfaces/change-bill-delivery.interface';

// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { JwkKey } from '@support/api/proxies/responses/api-2612.interface';
import { EncryptionService } from '@support/RSA-OAEP-encryption/services/encryption.service';

// Constants
import { BILLING_PAYMENT_CONSTANTS } from '@experience/modules/manage-profile/billing-and-payment/constants/billing-and-payment.constant';
// import {CHANNELS, SETTINGS} from '@environment/settings';
import { SAVED_PAYMENT_CARD_CONSTANTS } from '@experience/modules/manage-profile/billing-and-payment/saved-payment-card/saved-payment-card.constants';
import {
  BillingPreferenceRequest,
  BillingCommunicationAddresses,
} from '@support/api/proxies/requests/api-2037.interface';
import { UpdateBillingPreferenceResponse } from '@support/api/proxies/responses/api-2037.interface';
import { StoredPaymentMethodsContentInterface } from '@experience/modules/manage-profile/billing-and-payment/interfaces/stored-payment-methods.interface';
import { PaymentInformation } from '@support/payment/online-payment/models/card-entry-form.model';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { HttpResponse, HttpHeaders } from '@angular/common/http';
import { SharedPaymentService } from '@support/payment/shared-payment/services/shared-payment.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { PauseAndResumeResponse } from '@support/api/proxies/responses/api-2831.interface';

const CVV = 'cvv';

@Injectable()
export class BillingPaymentService {
  /**
   * Stores the data of selected payment method
   */
  public selectedStoredPayment: PaymentMethodsData;
  /**
   * Stores store payment method action
   */
  public storePaymentAction: string;
  /**
   * ngUnsubscribe subject for unscribe
   */
  private ngUnsubscribe: Subject<void>;
  public jwkKey: JwkKey;
  private encryptedCardNumber: string;
  constructor(
    private apiGatewayService: ApiGatewayService,
    private stateStoreUtils: StateStoreUtilsService,
    private contentProviderService: ContentProviderService,
    private formBuilder: UntypedFormBuilder,
    private formCreationService: FormCreationService,
    private commonApiService: CommonApiService,
    private logger: LoggerService,
    private encryptionService: EncryptionService,
    private sharedPaymentService: SharedPaymentService,
    private objectService: ObjectService
  ) {
    this.ngUnsubscribe = new Subject<void>();
  }

  /**
   * Returns parent form group for SavedPaymentMethod form
   * @returns FormGroup
   */
  public builSavedPaymentMethodForm(): UntypedFormGroup {
    const fieldValidationConstants = this.getFieldValidationConstants();
    const fullName = this.selectedStoredPayment
      ? this.selectedStoredPayment.firstName + ' ' + this.selectedStoredPayment.lastName
      : '';
    const cardNumber = this.selectedStoredPayment
      ? this.selectedStoredPayment.maskedCardNumber
      : '';
    const expirationMonthName = '';
    const expirationYear = '';
    const zipCode = this.selectedStoredPayment ? this.selectedStoredPayment.zipCode : '';

    const createFormControl = {
      fullName: this.formCreationService.getFormControl(
        fieldValidationConstants.FULL_NAME,
        fullName,
        true
      ),
      cardNumber: this.formCreationService.getFormControl(
        fieldValidationConstants.CREDICT_CARD_NUMBER,
        cardNumber,
        true
      ),
      expirationMonthName: [expirationMonthName, Validators.required],
      expirationYear: [expirationYear, Validators.required],
      zipCode: this.formCreationService.getFormControl(
        fieldValidationConstants.ZIP_CODE,
        zipCode,
        true
      ),
    };
    if (!this.selectedStoredPayment) {
      createFormControl[CVV] = this.formCreationService.getFormControl(
        fieldValidationConstants.CVV,
        '',
        true
      );
    }
    return this.formBuilder.group(createFormControl);
  }

  /**
   * Retrieves the fieldValidations constants from common API service
   * @returns ObjectInterface
   */
  public getFieldValidationConstants(): ObjectInterface {
    return this.commonApiService.getFieldValidationConstants();
  }

  /**
   * function to initialize the public jwk key
   */
  public initializePublicKey(): void {
    this.sharedPaymentService
      .getEncryptionKey()
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((jwkKey: JwkKey) => {
        this.jwkKey = jwkKey;
      });
  }
  /**
   * Used to get encrypted card number
   */
  private getEncryptedCardNumber(cardNumber: string): void {
    this.initializePublicKey();
    this.encryptionService
      .getEncryptedValue(this.jwkKey, cardNumber)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        (encryptedCardNumber: string) => {
          this.logger.info(
            SAVED_PAYMENT_CARD_CONSTANTS.SAVED_PAYMENT_MODULE,
            SAVED_PAYMENT_CARD_CONSTANTS.SAVED_PAYMENT_CARD_COMPONENT,
            `encrypted card number ${encryptedCardNumber}`
          );
          this.encryptedCardNumber = encryptedCardNumber;
        },
        () => {
          this.logger.info(
            SAVED_PAYMENT_CARD_CONSTANTS.SAVED_PAYMENT_MODULE,
            SAVED_PAYMENT_CARD_CONSTANTS.SAVED_PAYMENT_CARD_COMPONENT,
            `Encryption Failed`
          );
        }
      );
  }
  /**
   * Adds the new payment method
   * @returns Observable<SavePaymentMethod>
   */
  public postPaymentMethods(
    formGroup: UntypedFormGroup,
    paymentResponse: any,
    vestaToken?: string,
    cvvNumber?: string,
    paymentInformation?: PaymentInformation
  ): Observable<SavePaymentMethod> {
    const customerId = this.stateStoreUtils.getCustomerId();
    let cardNumber: string;
    let payload: Api144Payload;
    if (paymentInformation) {
      cardNumber = paymentInformation.encryptedCardNumber;
      payload = this.getPayInfoPayload(cvvNumber, payload, paymentInformation, customerId);
    } else {
      payload = {
        methodType: BILLING_PAYMENT_CONSTANTS.METHOD_TYPE,
        type: paymentResponse ? paymentResponse.cardType : this.selectedStoredPayment.cardType,
        paymentMethod: paymentResponse
          ? this.setPaymentMethodCode(paymentResponse.paymentMethodCode)
          : this.selectedStoredPayment.paymentMethod,
        nameOnCard: formGroup.controls[BILLING_PAYMENT_CONSTANTS.FULL_NAME].value,
        cardNumber: vestaToken || this.selectedStoredPayment.refNumber,
        expiration: {
          month: formGroup.controls[BILLING_PAYMENT_CONSTANTS.EXPIRATION_MONTH].value,
          year: formGroup.controls[BILLING_PAYMENT_CONSTANTS.EXPIRATION_YEAR].value,
        },
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          zipCode: formGroup.controls[BILLING_PAYMENT_CONSTANTS.ZIP_CODE].value,
        },
        commonCustomerId: this.getCommonCustomerId(),
      };
    }

    if (this.selectedStoredPayment) {
      const apiToCall = API_MAP.API_2619_getStoredPaymentMethods;
      payload.cardNumber = this.selectedStoredPayment.cardNumber;
      payload.customerId = customerId;
      payload.paymentMethodId = this.selectedStoredPayment.paymentDeviceId;
      return this.apiGatewayService.putRequest(apiToCall, payload).pipe(
        map((response: HttpResponse<SavePaymentMethod>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
    } else {
      const apiToCall = API_MAP.API_2618_getStoredPaymentMethods;
      payload.cardNumber = payload.cardNumber ? payload.cardNumber : cardNumber;
      payload.cvv = cvvNumber;
      payload.chargeAccountNumberIndicator = BILLING_PAYMENT_CONSTANTS.TEMP_TOKEN;
      payload.customerId = customerId;
      return this.apiGatewayService.postRequest(apiToCall, payload).pipe(
        map((response: HttpResponse<SavePaymentMethod>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
    }
  }

  /**
   * Function to return the required payment code
   * @returns object
   */
  public getPayInfoPayload(
    cvvNumber: string,
    payload: Api144Payload,
    paymentInformation: PaymentInformation,
    customerId: string
  ): Api144Payload {
    payload = this.createPayload(paymentInformation, customerId);
    cvvNumber = paymentInformation.cvv;
    this.getEncryptedCardNumber(paymentInformation.cardNumber);
    payload.cardNumber = this.encryptedCardNumber;
    return payload;
  }

  /**
   * Function to return the required payment code
   * @returns string
   */
  public setPaymentMethodCode(paymentmethodCode: string): string {
    if (paymentmethodCode === BILLING_PAYMENT_CONSTANTS.HYBRID_CARD) {
      return BILLING_PAYMENT_CONSTANTS.CREDIT_CARD;
    } else {
      return paymentmethodCode;
    }
  }

  /**
   * Function to get year options for drop down
   * @returns void
   */
  public getYearOptions(): SelectItem[] {
    const currentYear: number = new Date().getFullYear();
    const yearOptions: SelectItem[] = [];
    const totalYears = 21;
    for (let i = 0; i < totalYears; i++) {
      const year: number = currentYear + i;
      yearOptions.push({ label: year.toString(), value: year });
    }
    return yearOptions;
  }

  /**
   * Removes the  saved payment method and makes delete API call
   * @param skuId: selected Payment
   * @return observable payment method response
   */
  public removePaymentMethod(selectedPayment: PaymentMethodsData): Observable<object> {
    const customerId = this.stateStoreUtils.getCustomerId();
    const headers = new HttpHeaders();
    let request;
    const apiToCall = API_MAP.API_2620_getStoredPaymentMethods;
    request = {
      commonCustomerId: this.getCommonCustomerId(),
      customerId: customerId,
      cardNumber: selectedPayment.cardNumber,
      type: selectedPayment.cardType,
      storedPaymentId: selectedPayment.refNumber,
      paymentMethod: selectedPayment.paymentMethod,
    };
    return this.apiGatewayService.deleteRequest(apiToCall, request, headers).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * Fetches the modal content
   * @return removeModal
   */
  public getStaticModalContent(aemContentData: StoredPaymentMethodsContentInterface): StaticModal {
    const removeModal: StaticModal = {
      headerText: aemContentData.remove,
      bodyText: aemContentData.removePaymentMethodConfirmationMessage,
      disableDismiss: false,
      modalName: 'remove-modal',
      disableEsc: true,
      buttons: [
        {
          id: 'button1',
          type: 'submit',
          name: aemContentData.continue,
          class: 'btn btn-primary',
        },
      ],
      toggleModalEmitter: new EventEmitter(),
      dismissableMask: true,
      closableText: aemContentData.clickToClose,
    };
    return removeModal;
  }

  /**
   * Method to fetch Customer's Billing Preferences
   * @returns {Observable<BillingLanguagePreferenceResponse>}
   */
  public postCustomerAccountBillingPreferences(): Observable<BillingPreferenceResponse> {
    const reqObj = {
      accountNumber: this.stateStoreUtils.getAccountId(),
      ccid: this.stateStoreUtils.getCustomerId(),
    };
    return this.apiGatewayService
      .postRequest(API_MAP.API_2036_postcustomerAccountBillingPreferences, reqObj, null, null, null)
      .pipe(
        map((response: HttpResponse<Object>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Method for getting the AEM Contents of the configurable data
   * @returns Observable
   */
  public getBillingLanguageContentData(): Observable<ChangeBillingLanguageInterface> {
    return this.contentProviderService.getContentData().pipe(
      map((contentData: object) => {
        return this.contentProviderService.getConfigurableData(contentData);
      })
    );
  }

  /**
   * Method for getting the AEM Contents of the configurable data
   * @returns Observable
   */
  public getBillDeliveryContentData(): Observable<ChangeBillDeliveryInterface> {
    return this.contentProviderService.getContentData().pipe(
      map((contentData: object) => {
        return this.contentProviderService.getConfigurableData(contentData);
      })
    );
  }

  /**
   * Method to update the Customer's Billing Preferences API 2037
   * @returns {Observable<BillingLanguagePreferenceResponse>}
   */
  public updateCustomerAccountBillingPreference(
    language?: string,
    isBillingDelivery?: boolean,
    billDelivery?: string,
    billType?: string,
    address?: BillingCommunicationAddresses[]
  ): Observable<UpdateBillingPreferenceResponse> {
    let requestPayload: BillingPreferenceRequest = {};
    if (isBillingDelivery && billDelivery === BILLING_PAYMENT_CONSTANTS.PAPER) {
      requestPayload = {
        accountNumber: this.stateStoreUtils.getAccountId(),
        ccid: this.stateStoreUtils.getCustomerId(),
        billTypeCode: billType.toUpperCase(),
        mediaTypeCode: billDelivery.toUpperCase(),
        termsAcceptanceIndicator: true,
        billingCommunicationAddress: address,
      };
    } else if (isBillingDelivery && billDelivery === BILLING_PAYMENT_CONSTANTS.ELECTRONIC) {
      requestPayload = {
        accountNumber: this.stateStoreUtils.getAccountId(),
        ccid: this.stateStoreUtils.getCustomerId(),
        mediaTypeCode: billDelivery.toUpperCase(),
        termsAcceptanceIndicator: true,
      };
    } else {
      requestPayload = {
        accountNumber: this.stateStoreUtils.getAccountId(),
        ccid: this.stateStoreUtils.getCustomerId(),
        languageCode: language ? language : '',
      };
    }
    return this.apiGatewayService
      .postRequest(
        API_MAP.API_2037_updateCustomerAccountBillingPreferences,
        requestPayload,
        null,
        null,
        null
      )
      .pipe(
        map((response: HttpResponse<UpdateBillingPreferenceResponse>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * method to create payload of api 202
   * @returns Api144Payload
   */
  private createPayload(paymentInformation: PaymentInformation, customerId: string): Api144Payload {
    const radix: number = 10;
    const payload: Api144Payload = {
      methodType: BILLING_PAYMENT_CONSTANTS.METHOD_TYPE,
      type: paymentInformation.cardType,
      paymentMethod: paymentInformation.paymentMethodCode,
      nameOnCard: paymentInformation.fullName,
      cardNumber: paymentInformation.token,
      expiration: {
        month: parseInt(paymentInformation.expirationMonth, radix),
        year: parseInt(paymentInformation.expirationYear, radix),
      },
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zipCode: paymentInformation.zipCode,
      },
      commonCustomerId: this.getCommonCustomerId(),
    };
    return payload;
  }
  /**
   * used to get common customer id
   * @returns {string}
   */
  public getCommonCustomerId(): string {
    // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB) {
    const ccid = this.objectService.get(
      this.stateStoreUtils.getAccountLinesInfo(),
      'customer.ccid'
    );
    return ccid && this.stateStoreUtils.getCustomerId() !== SAVED_PAYMENT_CARD_CONSTANTS.ANON
      ? ccid
      : null;
    // } else {
    //   return (this.stateStoreUtils.getCustomerId() === SAVED_PAYMENT_CARD_CONSTANTS.ANON
    //     ? null : this.stateStoreUtils.getCustomerId());
    // }
  }

  /**
   * Method to set recording state boolean
   * @param {boolean} status pause status to set
   * @return void
   */
  public postPauseAndResume(status: boolean): void {
    this.commonApiService
      .postPauseAndResume(status)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((callData: PauseAndResumeResponse) => {
        // Response not considered
      });
  }
}
